<template>
  <div class="tr-view">
    <div v-if="store.loading && !store.overview" class="tr-state"><span class="mdi mdi-loading tr-state__spinner"></span></div>

    <div v-else-if="store.error" class="tr-state tr-state--message">
      <span class="mdi mdi-alert-circle-outline tr-state__icon"></span>
      <p>No se pudo cargar tu información.</p>
    </div>

    <div v-else-if="store.overview && !store.overview.contracts.length" class="tr-state tr-state--message">
      <span class="mdi mdi-file-document-outline tr-state__icon"></span>
      <p>Tu cuenta todavía no tiene un contrato con acceso al portal.</p>
    </div>

    <div v-else-if="store.overview" class="tr-scroll">
      <div class="tr-column">
        <TenantContractSelect
          :contracts="store.overview.contracts"
          :model-value="store.selectedContractId"
          @update:model-value="selectContract"
        />

        <!-- Métricas -->
        <section v-if="data" class="tr-metrics">
          <div class="tr-metric"><span class="tr-metric__value">{{ data.metrics.active }}</span><span class="tr-metric__label">Activas</span></div>
          <div class="tr-metric"><span class="tr-metric__value">{{ data.metrics.resolvedLast30 }}</span><span class="tr-metric__label">Resueltas (30 días)</span></div>
          <div class="tr-metric"><span class="tr-metric__value">{{ data.metrics.avgResolution || '—' }}</span><span class="tr-metric__label">Tiempo promedio</span></div>
        </section>

        <!-- Nueva solicitud -->
        <button v-if="canCreate" class="tr-new" :class="{ 'tr-new--open': formOpen }" @click="toggleForm">
          <span class="mdi" :class="formOpen ? 'mdi-close' : 'mdi-plus'"></span>
          {{ formOpen ? 'Cancelar' : 'Reportar problema' }}
        </button>
        <p v-else-if="selectedContract" class="tr-note">Solo puedes reportar problemas en un contrato vigente.</p>

        <div class="tr-form-wrap" :class="{ 'tr-form-wrap--open': formOpen && canCreate }">
          <div class="tr-form-inner">
            <form class="tr-form" @submit.prevent="submit">
              <span class="tr-label">Tipo de problema</span>
              <div class="tr-cats">
                <button
                  v-for="c in CATEGORIES" :key="c.key" type="button"
                  class="tr-cat" :class="{ 'tr-cat--active': form.category === c.key }"
                  @click="form.category = c.key"
                >
                  <span class="mdi" :class="c.icon"></span>
                  <span>{{ c.label }}</span>
                </button>
              </div>

              <label class="tr-label" for="tr-desc">Descripción</label>
              <textarea id="tr-desc" v-model="form.description" class="tr-textarea" rows="3" maxlength="2000" placeholder="Describe el problema con el mayor detalle posible..."></textarea>

              <div class="tr-photo">
                <input ref="fileInput" type="file" accept="image/*" capture="environment" class="tr-photo__input" @change="onFile" />
                <button type="button" class="tr-photo__btn" @click="fileInput.click()"><span class="mdi mdi-camera-outline"></span> Adjuntar foto</button>
                <span v-if="form.file" class="tr-photo__name">
                  {{ form.file.name }}
                  <button type="button" class="tr-photo__remove" aria-label="Quitar foto" @click="clearFile"><span class="mdi mdi-close"></span></button>
                </span>
              </div>

              <span class="tr-label">Urgencia</span>
              <div class="tr-urgency">
                <button
                  v-for="u in URGENCIES" :key="u.key" type="button"
                  class="tr-urgency__pill" :class="[`tr-urgency__pill--${u.key.toLowerCase()}`, { 'tr-urgency__pill--active': form.urgency === u.key }]"
                  @click="form.urgency = u.key"
                >{{ u.label }}</button>
              </div>

              <p v-if="formError" class="tr-error">{{ formError }}</p>
              <button type="submit" class="tr-submit" :disabled="submitting">{{ submitting ? 'Enviando…' : 'Enviar solicitud' }}</button>
            </form>
          </div>
        </div>

        <p v-if="confirmation" class="tr-confirm">
          Tu solicitud fue enviada · Número de ticket: {{ confirmation }} · Te notificaremos cuando haya una actualización
        </p>

        <!-- Lista -->
        <div v-if="loading" class="tr-state"><span class="mdi mdi-loading tr-state__spinner"></span></div>
        <div v-else-if="loadError" class="tr-state tr-state--message">
          <p>No se pudieron cargar tus solicitudes.</p>
          <button class="tr-retry" @click="load">Reintentar</button>
        </div>
        <template v-else-if="data">
          <div v-if="!data.tickets.length" class="tr-empty">Aún no has reportado ningún problema.</div>
          <article v-for="t in data.tickets" :key="t.id" class="tr-card" @click="openTicket(t.id)">
            <div class="tr-card__top">
              <span class="tr-card__code">{{ t.code }}</span>
              <span class="tr-card__date">{{ formatDate(t.createdAt) }}</span>
            </div>
            <div class="tr-card__title">{{ t.title }}</div>
            <div v-if="t.status" class="tr-card__bottom">
              <span class="tr-status" :style="{ background: STATUS[t.status].bg, color: STATUS[t.status].color }">{{ STATUS[t.status].label }}</span>
              <span v-if="t.technicianName" class="tr-card__tech">{{ t.technicianName }}</span>
              <span v-else-if="t.status !== 'RESOLVED'" class="tr-card__tech tr-card__tech--pending">Técnico por asignar</span>
            </div>
          </article>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { createPortalTicket, getPortalTickets } from '@/services/tenantPortal'
import { useTenantPortalStore } from '@/services/tenantPortalStore'
import TenantContractSelect from '@/components/TenantPortal/TenantContractSelect.vue'
import { TICKET_STATUS as STATUS, formatDate, ERROR_MESSAGES } from '@/components/TenantPortal/tenantTicketUi'

const router = useRouter()
const { state: store, loadOverview, selectContract } = useTenantPortalStore()

const CATEGORIES = [
  { key: 'ELECTRICAL', label: 'Eléctrico', icon: 'mdi-flash-outline' },
  { key: 'PLUMBING', label: 'Plomería', icon: 'mdi-water-outline' },
  { key: 'STRUCTURE', label: 'Estructura', icon: 'mdi-wall' },
  { key: 'SECURITY', label: 'Seguridad', icon: 'mdi-shield-lock-outline' },
  { key: 'CLEANING', label: 'Limpieza', icon: 'mdi-broom' },
  { key: 'OTHER', label: 'Otro', icon: 'mdi-dots-horizontal-circle-outline' },
]
const URGENCIES = [
  { key: 'NORMAL', label: 'Normal' },
  { key: 'URGENT', label: 'Urgente' },
  { key: 'CRITICAL', label: 'Crítico' },
]

const data = ref(null)
const loading = ref(false)
const loadError = ref(false)
const formOpen = ref(false)
const submitting = ref(false)
const formError = ref('')
const confirmation = ref('')
const fileInput = ref(null)
const form = reactive({ category: '', description: '', urgency: 'NORMAL', file: null })

const selectedContract = computed(() => store.overview?.contracts.find((c) => c.id === store.selectedContractId) || null)
const canCreate = computed(() => selectedContract.value?.status === 'ACTIVE')

async function load() {
  if (!store.selectedContractId) return
  loading.value = true
  loadError.value = false
  try {
    data.value = await getPortalTickets(store.selectedContractId)
  } catch {
    data.value = null
    loadError.value = true
  } finally {
    loading.value = false
  }
}

function toggleForm() {
  formOpen.value = !formOpen.value
  formError.value = ''
  if (formOpen.value) confirmation.value = ''
}

function onFile(event) {
  form.file = event.target.files?.[0] || null
}
function clearFile() {
  form.file = null
  if (fileInput.value) fileInput.value.value = ''
}

async function submit() {
  formError.value = ''
  if (!form.category) {
    formError.value = ERROR_MESSAGES.INVALID_CATEGORY
    return
  }
  if (form.description.trim().length < 5) {
    formError.value = ERROR_MESSAGES.DESCRIPTION_REQUIRED
    return
  }
  submitting.value = true
  try {
    const result = await createPortalTicket(store.selectedContractId, { ...form, description: form.description.trim() })
    confirmation.value = result.code
    form.category = ''
    form.description = ''
    form.urgency = 'NORMAL'
    clearFile()
    formOpen.value = false
    await load()
  } catch (err) {
    formError.value = ERROR_MESSAGES[err.message] || 'No se pudo enviar la solicitud. Intenta de nuevo.'
  } finally {
    submitting.value = false
  }
}

function openTicket(id) {
  router.push(`/inquilino/solicitudes/${id}`)
}

onMounted(async () => {
  try {
    await loadOverview()
  } catch {
    return
  }
  await load()
})
watch(() => store.selectedContractId, (next, prev) => {
  if (prev !== null && next !== prev) {
    formOpen.value = false
    confirmation.value = ''
    load()
  }
})
</script>

<style scoped>
.tr-view { flex: 1; min-height: 0; display: flex; flex-direction: column; background: #fff; }
.tr-scroll { flex: 1; overflow-y: auto; padding: 20px 16px 32px; }
.tr-column { max-width: 720px; margin: 0 auto; display: flex; flex-direction: column; gap: 14px; }
.tr-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 32px 16px; color: #94a3b8; flex: 1; }
.tr-state--message { text-align: center; font: 400 13px Inter, sans-serif; }
.tr-state__icon { font-size: 32px; }
.tr-state__spinner { font-size: 28px; animation: tr-spin 1s linear infinite; }
@keyframes tr-spin { to { transform: rotate(360deg); } }
.tr-retry { height: 32px; padding: 0 14px; border: none; border-radius: 6px; background: #3b82f6; color: #fff; font: 500 13px Inter, sans-serif; cursor: pointer; }

.tr-metrics { display: flex; justify-content: space-around; gap: 8px; padding: 10px 0; border-bottom: 1px solid #f1f5f9; }
.tr-metric { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.tr-metric__value { font: 400 13px var(--font-mono); color: #64748b; }
.tr-metric__label { font: 400 11px Inter, sans-serif; color: #94a3b8; }

.tr-new { height: 44px; border: none; border-radius: 8px; background: #3b82f6; color: #fff; font: 500 14px Inter, sans-serif; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; }
.tr-new--open { background: #64748b; }
.tr-note { margin: 0; text-align: center; font: 400 12px Inter, sans-serif; color: #94a3b8; }

/* Panel que se despliega bajo el botón (200 ms), sin modal. */
.tr-form-wrap { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 200ms ease; }
.tr-form-wrap--open { grid-template-rows: 1fr; }
.tr-form-inner { overflow: hidden; min-height: 0; }
.tr-form { display: flex; flex-direction: column; gap: 10px; padding: 14px; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; }
.tr-label { font: 500 11px Inter, sans-serif; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.4px; }
.tr-cats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.tr-cat { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 10px 4px; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; color: #64748b; font: 500 12px Inter, sans-serif; cursor: pointer; }
.tr-cat .mdi { font-size: 22px; }
.tr-cat--active { border-color: #3b82f6; background: #eff6ff; color: #1d4ed8; }
.tr-textarea { width: 100%; padding: 10px; border: 1px solid #e2e8f0; border-radius: 6px; font: 400 13px Inter, sans-serif; color: #1e3a5f; resize: vertical; }
.tr-textarea:focus { outline: none; border-color: #3b82f6; }
.tr-photo { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.tr-photo__input { display: none; }
.tr-photo__btn { height: 34px; padding: 0 12px; border: 1px dashed #94a3b8; border-radius: 6px; background: #fff; color: #64748b; font: 500 12px Inter, sans-serif; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.tr-photo__name { font: 400 12px Inter, sans-serif; color: #1e3a5f; display: flex; align-items: center; gap: 4px; max-width: 100%; word-break: break-all; }
.tr-photo__remove { border: none; background: none; color: #94a3b8; cursor: pointer; font-size: 14px; }
.tr-urgency { display: flex; gap: 8px; }
.tr-urgency__pill { flex: 1; height: 34px; border: 1px solid #e2e8f0; border-radius: 17px; background: #fff; color: #64748b; font: 500 12px Inter, sans-serif; cursor: pointer; }
.tr-urgency__pill--active.tr-urgency__pill--normal { background: #dbeafe; border-color: #3b82f6; color: #1d4ed8; }
.tr-urgency__pill--active.tr-urgency__pill--urgent { background: #fef3c7; border-color: #f59e0b; color: #b45309; }
.tr-urgency__pill--active.tr-urgency__pill--critical { background: #fee2e2; border-color: #ef4444; color: #b91c1c; }
.tr-error { margin: 0; font: 400 12px Inter, sans-serif; color: #b91c1c; }
.tr-submit { height: 40px; border: none; border-radius: 8px; background: #1e3a5f; color: #fff; font: 500 13px Inter, sans-serif; cursor: pointer; }
.tr-submit:disabled { opacity: 0.6; cursor: not-allowed; }

.tr-confirm { margin: 0; padding: 10px 12px; border: 1px solid #bbf7d0; border-radius: 8px; background: #f0fdf4; color: #15803d; font: 400 12px/1.5 Inter, sans-serif; }

.tr-empty { text-align: center; padding: 24px; font: 400 12px Inter, sans-serif; color: #94a3b8; }
.tr-card { padding: 12px 14px; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; cursor: pointer; display: flex; flex-direction: column; gap: 6px; }
.tr-card:hover { border-color: #cbd5e1; box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06); }
.tr-card__top { display: flex; justify-content: space-between; align-items: center; }
.tr-card__code { font: 400 12px var(--font-mono); color: #94a3b8; }
.tr-card__date { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.tr-card__title { font: 500 13px/1.4 Inter, sans-serif; color: #1e3a5f; }
.tr-card__bottom { display: flex; flex-direction: column; align-items: center; gap: 4px; margin-top: 4px; }
.tr-status { width: 100%; height: 28px; display: flex; align-items: center; justify-content: center; border-radius: 14px; font: 500 12px Inter, sans-serif; }
.tr-card__tech { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.tr-card__tech--pending { color: #b45309; }

@media (max-width: 768px) {
  .tr-scroll { padding: 14px 12px 24px; }
}
</style>